import { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';

interface TooltipPortalProps {
  children: React.ReactNode;
}

// Global reference counter for portal container usage
let portalRefCount = 0;
let portalContainer: HTMLElement | null = null;

// Get or create the shared portal container
function getOrCreatePortalContainer(): HTMLElement {
  if (!portalContainer || !document.body.contains(portalContainer)) {
    // Create new container if it doesn't exist or was removed
    portalContainer = document.createElement('div');
    portalContainer.id = 'tooltip-portal-root';
    portalContainer.style.position = 'absolute';
    portalContainer.style.top = '0';
    portalContainer.style.left = '0';
    portalContainer.style.zIndex = '9999';
    portalContainer.style.pointerEvents = 'none';
    
    try {
      document.body.appendChild(portalContainer);
    } catch (error) {
      console.warn('Failed to append portal container:', error);
    }
  }
  
  return portalContainer;
}

// Safely remove portal container with defensive checks
function cleanupPortalContainer(): void {
  if (!portalContainer) return;
  
  // Use requestAnimationFrame to ensure cleanup happens after React portal cleanup
  requestAnimationFrame(() => {
    // Double-check reference count and container state
    if (portalRefCount <= 0 && portalContainer) {
      try {
        // Verify the container is still a child of document.body
        if (document.body.contains(portalContainer)) {
          // Additional safety: check if container is truly empty
          if (portalContainer.children.length === 0) {
            document.body.removeChild(portalContainer);
          }
        }
      } catch (error) {
        // Gracefully handle any DOM errors
        console.warn('Portal container cleanup error (safe to ignore):', error);
      } finally {
        // Reset container reference regardless of removal success
        portalContainer = null;
      }
    }
  });
}

export default function TooltipPortal({ children }: TooltipPortalProps) {
  const [mounted, setMounted] = useState(false);
  const [portalElement, setPortalElement] = useState<HTMLElement | null>(null);

  useEffect(() => {
    // Increment reference count
    portalRefCount++;
    
    try {
      // Get or create the shared container
      const container = getOrCreatePortalContainer();
      setPortalElement(container);
      setMounted(true);
    } catch (error) {
      console.warn('Failed to setup portal:', error);
      // Decrement count if setup failed
      portalRefCount--;
    }

    return () => {
      // Decrement reference count
      portalRefCount--;
      
      // Only attempt cleanup if this was the last portal
      if (portalRefCount <= 0) {
        cleanupPortalContainer();
      }
    };
  }, []);

  // Defensive rendering - ensure we have valid portal element
  if (!mounted || !portalElement) {
    return null;
  }

  try {
    return createPortal(children, portalElement);
  } catch (error) {
    console.warn('Portal rendering error:', error);
    return null;
  }
}